import airport from '@rsos/assets/icons/src/mapSearchCategories/airport.svg';
import animalShelter from '@rsos/assets/icons/src/mapSearchCategories/animal-shelter.svg';
import bridge from '@rsos/assets/icons/src/mapSearchCategories/bridge.svg';
import busStop from '@rsos/assets/icons/src/mapSearchCategories/bus-stop.svg';
import campground from '@rsos/assets/icons/src/mapSearchCategories/campground.svg';
import cemetery from '@rsos/assets/icons/src/mapSearchCategories/cemetery.svg';
import church from '@rsos/assets/icons/src/mapSearchCategories/church.svg';
import emsStation from '@rsos/assets/icons/src/mapSearchCategories/ems-station.svg';
import fireStation from '@rsos/assets/icons/src/mapSearchCategories/fire-station.svg';
import gasStation from '@rsos/assets/icons/src/mapSearchCategories/gas-station.svg';
import government from '@rsos/assets/icons/src/mapSearchCategories/government.svg';
import heliport from '@rsos/assets/icons/src/mapSearchCategories/heliport.svg';
import hospital from '@rsos/assets/icons/src/mapSearchCategories/hospital.svg';
import hotel from '@rsos/assets/icons/src/mapSearchCategories/hotel.svg';
import hydrant from '@rsos/assets/icons/src/mapSearchCategories/hydrant.svg';
import lighthouse from '@rsos/assets/icons/src/mapSearchCategories/lighthouse.svg';
import mall from '@rsos/assets/icons/src/mapSearchCategories/mall.svg';
import marina from '@rsos/assets/icons/src/mapSearchCategories/marina.svg';
import parcel from '@rsos/assets/icons/src/mapSearchCategories/parcel.svg';
import park from '@rsos/assets/icons/src/mapSearchCategories/park.svg';
import parking from '@rsos/assets/icons/src/mapSearchCategories/parking.svg';
import policeStation from '@rsos/assets/icons/src/mapSearchCategories/police-station.svg';
import powerPlant from '@rsos/assets/icons/src/mapSearchCategories/power-plant.svg';
import prison from '@rsos/assets/icons/src/mapSearchCategories/prison.svg';
import rail from '@rsos/assets/icons/src/mapSearchCategories/rail.svg';
import recyclingCenter from '@rsos/assets/icons/src/mapSearchCategories/recycling-center.svg';
import residence from '@rsos/assets/icons/src/mapSearchCategories/residence.svg';
import restaurant from '@rsos/assets/icons/src/mapSearchCategories/restaurant.svg';
import school from '@rsos/assets/icons/src/mapSearchCategories/school.svg';
import shelter from '@rsos/assets/icons/src/mapSearchCategories/shelter.svg';
import stadium from '@rsos/assets/icons/src/mapSearchCategories/stadium.svg';
import tower from '@rsos/assets/icons/src/mapSearchCategories/tower.svg';
import trail from '@rsos/assets/icons/src/mapSearchCategories/trail.svg';
import waterTreatment from '@rsos/assets/icons/src/mapSearchCategories/water-treatment.svg';

/**
 * One entry per silhouette icon in
 * `libs/assets/icons/src/mapSearchCategories/`. Consumers (the search
 * dropdown, primarily) substring-match each `keyword` against a GIS
 * layer title — case-insensitive — to pick the icon for a result row.
 *
 * Keep `keywords` lowercase. They're matched as plain substrings, so
 * ordering inside this array determines tie-breaks: the first entry
 * whose keyword list contains a hit wins. Put more specific
 * categories before more generic ones (e.g. `fire-station` before a
 * hypothetical `fire` entry) so the wrong icon doesn't preempt the
 * right one. Categories that need to win against each other for the
 * same word (`heliport` vs. `airport`) live in the order a designer
 * would expect to see picked.
 */
export interface MapSearchCategoryIcon {
  /** Stable category key — matches the SVG basename (kebab-case). */
  key: string;
  /**
   * Human-readable display name for this category. Used by UI
   * surfaces that need to show the category alongside (or instead of)
   * the icon — legends, tooltips, accessible names, filter chips, and
   * "result type → icon" mappings driven off feature attributes
   * rather than free-text layer titles.
   */
  label: string;
  /** Bundled URL to the SVG asset. */
  icon: string;
  /**
   * Substrings checked against a layer title (lowercased). A match
   * means this icon represents that layer.
   */
  keywords: readonly string[];
}

/**
 * Source of truth for category → icon + keyword vocab. Mirrors the
 * design-review preview at
 * `libs/assets/icons/src/mapSearchCategories/preview.html`; whenever a
 * keyword is added here it should be added there too (or vice-versa)
 * so the preview keeps showing what's actually wired up.
 *
 * Order matters: the resolver walks this list head-to-tail and the
 * first entry with a keyword substring hit wins. Several entries are
 * deliberately positioned to short-circuit a more generic neighbour
 * (e.g. `parking` before `parcel` so "Parking Lot 5" isn't claimed by
 * `parcel`'s `lot` keyword; `animal-shelter` before `shelter` so
 * "Animal Shelter" doesn't pick up the evacuation-shelter icon).
 */
export const MAP_SEARCH_CATEGORY_ICONS = [
  {
    key: 'hydrant',
    label: 'Hydrant',
    icon: hydrant,
    keywords: ['hydrant'],
  },
  // `parking` sits ahead of `parcel` because parcel's `lot` keyword
  // would otherwise swallow titles like "Parking Lot 5". Both
  // categories still resolve correctly for their own native phrases.
  {
    key: 'parking',
    label: 'Parking',
    icon: parking,
    keywords: ['parking', 'parking lot', 'parking garage', 'parking structure'],
  },
  {
    key: 'parcel',
    label: 'Parcel',
    icon: parcel,
    keywords: ['parcel', 'apn', 'lot', 'plat', 'property'],
  },
  {
    key: 'residence',
    label: 'Residence',
    icon: residence,
    keywords: ['residence', 'address', 'home', 'dwelling', 'house'],
  },
  {
    key: 'school',
    label: 'School',
    icon: school,
    keywords: [
      'school',
      'elementary',
      'middle',
      'high',
      'university',
      'college',
      'preschool',
      'daycare',
      'academy',
    ],
  },
  {
    key: 'hospital',
    label: 'Hospital',
    icon: hospital,
    keywords: [
      'hospital',
      'clinic',
      'urgent care',
      'er',
      'icu',
      'medical center',
    ],
  },
  {
    key: 'ems-station',
    label: 'EMS Station',
    icon: emsStation,
    keywords: ['ems', 'ambulance', 'paramedic', 'medic', 'first aid'],
  },
  // `animal-shelter` is intentionally listed before `shelter` so a
  // layer titled "Animal Shelter" lands the kennel/paw icon rather
  // than the generic evacuation-shelter silhouette.
  {
    key: 'animal-shelter',
    label: 'Animal Shelter',
    icon: animalShelter,
    keywords: [
      'animal shelter',
      'animal control',
      'humane society',
      'kennel',
      'spca',
      'pound',
    ],
  },
  {
    key: 'shelter',
    label: 'Shelter',
    icon: shelter,
    keywords: ['shelter', 'refuge', 'evacuation', 'safe haven'],
  },
  // `fire-station` is intentionally listed before any future `fire`
  // entry: a layer titled "Fire Station 12" must resolve to the
  // station icon, not a generic flame.
  {
    key: 'fire-station',
    label: 'Fire Station',
    icon: fireStation,
    keywords: ['fire station', 'fire dept', 'firehouse', 'fire'],
  },
  {
    key: 'police-station',
    label: 'Police Station',
    icon: policeStation,
    keywords: ['police station', 'police dept', 'sheriff', 'precinct'],
  },
  {
    key: 'cemetery',
    label: 'Cemetery',
    icon: cemetery,
    keywords: ['cemetery', 'graveyard', 'burial', 'mausoleum'],
  },
  {
    key: 'church',
    label: 'Place of Worship',
    icon: church,
    keywords: [
      'church',
      'temple',
      'mosque',
      'synagogue',
      'chapel',
      'cathedral',
    ],
  },
  {
    key: 'government',
    label: 'Government',
    icon: government,
    keywords: [
      'government',
      'city hall',
      'courthouse',
      'municipal',
      'civic',
      'federal',
    ],
  },
  {
    key: 'prison',
    label: 'Prison',
    icon: prison,
    keywords: [
      'prison',
      'jail',
      'penitentiary',
      'correctional',
      'corrections',
      'detention center',
    ],
  },
  // `mall` deliberately avoids the standalone `mall` keyword because
  // it would substring-match "small". The longer phrases plus a plain
  // `shopping` cover the realistic layer-title shapes.
  {
    key: 'mall',
    label: 'Shopping Center',
    icon: mall,
    keywords: [
      'shopping mall',
      'shopping center',
      'shopping plaza',
      'outlet mall',
      'shopping',
    ],
  },
  {
    key: 'restaurant',
    label: 'Restaurant',
    icon: restaurant,
    keywords: ['restaurant', 'dining', 'cafe', 'diner', 'bistro', 'eatery'],
  },
  {
    key: 'hotel',
    label: 'Hotel',
    icon: hotel,
    keywords: ['hotel', 'motel', 'lodging', 'hostel', 'resort'],
  },
  {
    key: 'stadium',
    label: 'Stadium',
    icon: stadium,
    keywords: ['stadium', 'arena', 'ballpark', 'coliseum', 'amphitheater'],
  },
  {
    key: 'lighthouse',
    label: 'Lighthouse',
    icon: lighthouse,
    keywords: ['lighthouse'],
  },
  // `campground` avoids the bare `camp` substring so it doesn't claim
  // university "campus" titles.
  {
    key: 'campground',
    label: 'Campground',
    icon: campground,
    keywords: ['campground', 'campsite', 'rv park', 'tent site'],
  },
  // `trail` is intentionally listed before `park` (which used to
  // include `trail` in its own keyword set) so "Hiking Trail" picks
  // the dedicated trail icon.
  {
    key: 'trail',
    label: 'Trail',
    icon: trail,
    keywords: ['trail', 'hiking', 'footpath', 'pathway', 'bike path'],
  },
  {
    key: 'park',
    label: 'Park',
    icon: park,
    keywords: ['park', 'greenspace', 'recreation', 'forest'],
  },
  {
    key: 'gas-station',
    label: 'Gas Station',
    icon: gasStation,
    keywords: ['gas station', 'fuel', 'petrol', 'filling station'],
  },
  // `bus-stop` precedes `rail` because rail's `station`/`transit`
  // keywords would otherwise sweep up "Bus Station" / "Transit Stop".
  {
    key: 'bus-stop',
    label: 'Bus Stop',
    icon: busStop,
    keywords: ['bus stop', 'bus station', 'bus terminal', 'bus shelter'],
  },
  // `airport` lists `heliport` in its synonym set per design, but we
  // keep `heliport` ordered after `airport` here so that a layer
  // titled simply "Heliports" still wins via its own dedicated icon.
  // (Substring matcher walks this array head-to-tail.)
  {
    key: 'airport',
    label: 'Airport',
    icon: airport,
    keywords: ['airport', 'airfield', 'runway'],
  },
  {
    key: 'heliport',
    label: 'Heliport',
    icon: heliport,
    keywords: ['heliport', 'helipad', 'helicopter', 'landing zone', 'lz'],
  },
  {
    key: 'rail',
    label: 'Rail',
    icon: rail,
    keywords: ['rail', 'railroad', 'train', 'station', 'track', 'transit'],
  },
  // `marina` deliberately omits the bare `port` substring — it would
  // collide with "airport", "heliport", "transport", "report", etc.
  // The longer phrases plus `seaport` cover real maritime titles.
  {
    key: 'marina',
    label: 'Marina',
    icon: marina,
    keywords: [
      'marina',
      'harbor',
      'dock',
      'pier',
      'wharf',
      'boat ramp',
      'boating',
      'seaport',
    ],
  },
  {
    key: 'bridge',
    label: 'Bridge',
    icon: bridge,
    keywords: ['bridge', 'overpass', 'viaduct'],
  },
  {
    key: 'tower',
    label: 'Tower',
    icon: tower,
    keywords: ['tower', 'cell tower', 'radio tower', 'antenna'],
  },
  {
    key: 'power-plant',
    label: 'Power Plant',
    icon: powerPlant,
    keywords: ['power plant', 'substation', 'electric', 'utility', 'factory'],
  },
  // `recycling-center` skips "transfer station" intentionally —
  // `station` is already a `rail` keyword and `rail` sits earlier in
  // the array.
  {
    key: 'recycling-center',
    label: 'Recycling Center',
    icon: recyclingCenter,
    keywords: ['recycling', 'landfill', 'waste management', 'dump site'],
  },
  {
    key: 'water-treatment',
    label: 'Water Treatment',
    icon: waterTreatment,
    keywords: ['water treatment', 'wastewater', 'sewage', 'reservoir'],
  },
] as const satisfies readonly MapSearchCategoryIcon[];

/** Type-level union of every category key declared above. */
export type MapSearchCategoryKey =
  (typeof MAP_SEARCH_CATEGORY_ICONS)[number]['key'];

/**
 * Lookup table keyed by the stable `key` field. Useful when a result
 * already carries its category (e.g. a typed feature attribute) and
 * the caller wants the icon URL or display label without paying the
 * cost of the substring resolver.
 */
export const MAP_SEARCH_CATEGORY_BY_KEY: Readonly<
  Record<MapSearchCategoryKey, MapSearchCategoryIcon>
> = Object.freeze(
  Object.fromEntries(MAP_SEARCH_CATEGORY_ICONS.map(c => [c.key, c])),
) as unknown as Readonly<Record<MapSearchCategoryKey, MapSearchCategoryIcon>>;

/**
 * Direct icon lookup for a known category key. Returns `undefined`
 * when the key isn't a registered category so callers can fall back
 * to substring matching or render a generic icon.
 */
export function getCategoryIconByKey(
  key: string | null | undefined,
): string | undefined {
  if (!key) return undefined;
  return MAP_SEARCH_CATEGORY_BY_KEY[key as MapSearchCategoryKey]?.icon;
}

/**
 * Resolve a single category-icon URL for an arbitrary text snippet
 * (typically a GIS layer / sublayer title, but any string works).
 *
 * The match is plain case-insensitive substring containment, walking
 * `MAP_SEARCH_CATEGORY_ICONS` head-to-tail and returning the first
 * entry whose keyword list lands a hit. Returns `undefined` when no
 * keyword matches so callers can render a generic fallback icon.
 *
 * Multiple text fragments can be passed (e.g. layer title + spatial
 * object name + kind) and the first non-empty fragment that yields a
 * match wins. This lets call sites supply a prioritized list of
 * sources without each having to short-circuit themselves.
 */
export function resolveCategoryIcon(
  ...candidates: readonly (string | null | undefined)[]
): string | undefined {
  for (const candidate of candidates) {
    if (!candidate) continue;
    const haystack = candidate.toLowerCase();
    for (const cat of MAP_SEARCH_CATEGORY_ICONS) {
      if (cat.keywords.some(k => haystack.includes(k))) return cat.icon;
    }
  }
  return undefined;
}
